import type { CacheObject } from '@/services/CacheService/types/CacheObject';
import { CACHE_SERVICE_DEFAULT_TIME_OF_LIFE } from '@/services/CacheService/constants/cacheDefaultTimeOfLife';
import {
  CACHE_SERVICE_TIMESTAMP_KEY,
  CACHE_SERVICE_TIME_KEY,
  CACHE_SERVICE_VERSION_KEY,
} from '@/services/CacheService/constants/cacheServiceKeys';

const CACHE_STORAGE_KEY = '__CACHE__';

class CacheService {
  public static set<T>(
    variable: string,
    value: unknown,
    cacheTimeMs: number = CACHE_SERVICE_DEFAULT_TIME_OF_LIFE,
    version: number | string = 1,
  ) {
    try {
      this.init();

      if (value && cacheTimeMs > 0) {
        const data = {
          [CACHE_SERVICE_TIMESTAMP_KEY]: this.generateTimestamp(),
          [CACHE_SERVICE_TIME_KEY]: cacheTimeMs,
          [CACHE_SERVICE_VERSION_KEY]: version,
          value,
        } as CacheObject<T>;

        this.updateStorage({
          ...this.getStorage(),
          [variable]: data,
        });
      } else {
        localStorage.removeItem(variable);
      }
    } catch (error) {
      console.error(error);
    }
  }

  public static get<T = unknown>(
    variable: string,
    shouldRemove: boolean = false,
    version: number | string = 1,
  ): T | undefined {
    try {
      this.init();

      if (shouldRemove) this.remove(variable);

      const storage = this.getStorage();

      const parsedValue = storage[variable];

      if (parsedValue) {
        const currentTimeStamp = this.generateTimestamp();
        const cachedTimeStamp = parsedValue[CACHE_SERVICE_TIMESTAMP_KEY];
        const cachedTimeLife = parsedValue[CACHE_SERVICE_TIME_KEY];
        const cachedVersion = parsedValue[CACHE_SERVICE_VERSION_KEY];

        if (
          +currentTimeStamp - +cachedTimeStamp <= +cachedTimeLife &&
          JSON.stringify(version) === JSON.stringify(cachedVersion)
        ) {
          return parsedValue.value as T;
        }

        this.remove(variable);
      }
    } catch (error) {
      console.error(error);
    }
    return undefined;
  }

  public static remove(variable: string) {
    try {
      this.init();

      const storage = this.getStorage();

      if (variable in storage) delete storage[variable];

      this.updateStorage(storage);
    } catch (error) {
      console.error(error);
    }
  }

  public static minutes(minutes: number = 1) {
    return 60000 * minutes;
  }

  private static init() {
    if (!localStorage.getItem(CACHE_STORAGE_KEY)) localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify({}));
  }

  private static getStorage(): Record<string, CacheObject<unknown>> {
    this.init();

    return JSON.parse(localStorage.getItem(CACHE_STORAGE_KEY) as string);
  }

  private static updateStorage(storage: Record<string, CacheObject<unknown>>) {
    this.init();

    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(storage));
  }

  private static generateTimestamp() {
    return new Date().getTime();
  }
}

export default CacheService;
